import { mediaUrl } from './index'
import { t } from 'i18next'

export const DEFAULT_OG_IMAGE =
  'https://data.wowaudit.com/img/new-logo-icon.png'
const CREST_HOST = 'https://data.wowaudit.com/crests'

export type SeoValues = {
  title?: string
  description?: string
  robots?: string
  ogType?: string
  ogImage?: string
  siteName?: string
}

export const htmlExcerpt = (html?: string | null, limit = 160) => {
  if (!html) return undefined
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text) return undefined
  return text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}…` : text
}

export const siteTitle = (
  parts: Array<string | undefined | null>,
  customDomain?: wowaudit.CustomDomain,
) => {
  const titles = parts.filter((part): part is string => !!part)
  if (customDomain) return titles.join(' | ')
  if (titles[titles.length - 1] === 'wowaudit') return titles.join(' | ')
  if (titles[0]?.startsWith('wowaudit')) return titles.join(' | ')
  return [...titles, 'wowaudit'].join(' | ')
}

export const realmLabel = (
  realm?: wowaudit.Realm,
  uriRealm?: string,
  uriRegion?: string,
) =>
  realm?.nameWithRegion ||
  (uriRealm && uriRegion
    ? `${uriRealm} (${uriRegion.toUpperCase()})`
    : undefined)

export const characterOgImage = (
  character?: wowaudit.Character | wowaudit.CharacterReference,
  gameVersion?: wowaudit.PageInfo['gameVersion'],
) => {
  if (!character?.mediaZone) return undefined
  return mediaUrl(character, gameVersion === 'live' ? 'main-raw' : 'avatar')
}

export const guildOgImage = (
  guild?: wowaudit.Guild | wowaudit.GuildReference,
  customDomain?: wowaudit.CustomDomain,
) => {
  const logo =
    customDomain?.darkModeHeaderLogoPath ||
    customDomain?.lightModeHeaderLogoPath
  if (logo) return logo
  if (!guild) return undefined
  const filename =
    guild.bannerFilename ||
    `default-${(guild.faction || 'Alliance').toLowerCase()}.png`
  return `${CREST_HOST}/${filename}`
}

export const characterDescription = (
  character?: wowaudit.Character | wowaudit.CharacterReference,
  gameVersion?: wowaudit.PageInfo['gameVersion'],
) => {
  if (!character) return undefined
  const details = 'details' in character ? character.details : undefined
  const identity = [
    character.level ? `Level ${character.level}` : undefined,
    character.race,
    character.currentSpec?.name || character.characterClass,
  ]
    .filter(Boolean)
    .join(' ')
  const realm = realmLabel(character.realm)
  const sentence = [identity, realm ? `on ${realm}` : undefined]
    .filter(Boolean)
    .join(' ')

  const extras = [
    details?.currentItemLevel
      ? `Item level ${details.currentItemLevel}`
      : undefined,
    gameVersion === 'live' && details?.mythicRating
      ? `Mythic+ rating ${Math.round(details.mythicRating)}`
      : undefined,
    'guildReference' in character && character.guildReference?.name
      ? `Member of ${character.guildReference.name}`
      : undefined,
  ].filter(Boolean)

  return [sentence, extras.join(', ')].filter(Boolean).join('. ').concat('.')
}

export const guildDescription = (guild?: wowaudit.Guild) => {
  if (!guild) return undefined
  const realm = realmLabel(guild.realm)
  const identity = [
    guild.faction,
    'guild',
    guild.name,
    realm ? `on ${realm}` : undefined,
  ]
    .filter(Boolean)
    .join(' ')
  const extras = [
    guild.guildMembers ? `${guild.guildMembers} members` : undefined,
    guild.teamReferences ? `${guild.teamReferences.length} teams` : undefined,
    htmlExcerpt(guild.motd),
  ].filter(Boolean)

  return [identity, extras.join('. ')].filter(Boolean).join('. ').concat('.')
}

export const teamDescription = (
  team?: wowaudit.Team | wowaudit.TeamReference,
  guild?: wowaudit.Guild,
) => {
  if (!team) return undefined
  const realm = realmLabel(guild?.realm || team.ownerReference?.realm)
  const guildName = guild?.name || team.ownerReference?.name
  const identity = [
    team.name,
    guildName ? 'team in' : undefined,
    guildName,
    realm ? `on ${realm}` : undefined,
  ]
    .filter(Boolean)
    .join(' ')
  const extras = [htmlExcerpt(team.description)].filter(Boolean)

  return [identity, extras.join('. ')].filter(Boolean).join('. ').concat('.')
}

export const characterSeo = (
  character: wowaudit.Character | wowaudit.CharacterReference | undefined,
  options: {
    uriName?: string
    uriRealm?: string
    uriRegion?: string
    gameVersion?: wowaudit.PageInfo['gameVersion']
  },
): SeoValues => {
  const name = character?.name || options.uriName
  const realm = realmLabel(
    character?.realm,
    options.uriRealm,
    options.uriRegion,
  )
  if (!character?.id || character.gdprStatus !== 'exists') {
    return {
      title: name || t('dashboard.character.not-found'),
      robots: 'noindex, nofollow',
    }
  }

  return {
    title: [name, realm].filter(Boolean).join(' — '),
    description: characterDescription(character, options.gameVersion),
    ogImage: characterOgImage(character, options.gameVersion),
    robots: 'index, follow',
  }
}

export const stripPrimaryMarkup = (value: string) =>
  value.replace(/<\/?primary>/g, '')

export const homeDocumentTitle = (hero: string) => {
  const tagline = stripPrimaryMarkup(hero)
  return tagline ? `wowaudit — ${tagline}` : 'wowaudit'
}

const teamPageSeo = ({
  rest,
  guild,
  team,
  customDomain,
  heading,
  image,
  siteName,
}: {
  rest: string[]
  guild?: wowaudit.Guild
  team?: wowaudit.Team | wowaudit.TeamReference
  customDomain?: wowaudit.CustomDomain
  heading: string
  image?: string
  siteName?: string
}): SeoValues => {
  const names = [team?.name, heading].filter(Boolean)
  if (!team?.id) {
    return {
      title: siteTitle(names, customDomain),
      robots: 'noindex, nofollow',
      ogImage: image,
      siteName,
    }
  }

  const privatePath =
    rest[0] === 'settings' ||
    rest[0] === 'applicants' ||
    (rest[0] === 'apply' && !!rest[1]) ||
    newsEditorPath(rest)

  return {
    title: siteTitle(names, customDomain),
    description: teamDescription(team, guild),
    ogImage: image,
    robots: privatePath ? 'noindex, nofollow' : 'index, follow',
    siteName,
  }
}

const GUILD_SUBPAGE_KEYS: Record<string, string> = {
  members: 'shared.members',
  teams: 'shared.teams',
  news: 'shared.news',
  apply: 'shared.apply',
  housing: 'shared.housing',
  premium: 'shared.premium',
}

const newsEditorPath = (segments: string[]) =>
  segments.includes('edit') || segments[segments.length - 1] === 'new'

export const guildDashboardSeo = ({
  pathname,
  guild,
  team,
  customDomain,
}: {
  pathname: string
  guild?: wowaudit.Guild
  team?: wowaudit.Team | wowaudit.TeamReference
  customDomain?: wowaudit.CustomDomain
}): SeoValues => {
  const segments = pathname.split('/').filter(Boolean)
  const rest = customDomain
    ? segments
    : segments[0] === 'guild'
    ? segments.slice(4)
    : segments
  const heading = [guild?.name, realmLabel(guild?.realm)]
    .filter(Boolean)
    .join(' — ')
  const image = guildOgImage(guild, customDomain)
  const description = guildDescription(guild)
  const siteName = customDomain
    ? guild?.name || customDomain.team?.name
    : undefined

  if (!guild?.id || guild.exists === false) {
    return {
      title: siteTitle(
        [heading || t('dashboard.guild.not-found')],
        customDomain,
      ),
      robots: 'noindex, nofollow',
      siteName,
    }
  }

  if (
    rest[0] === 'settings' ||
    rest[0] === 'onboarding' ||
    newsEditorPath(rest)
  ) {
    return {
      title: siteTitle(
        [t(GUILD_SUBPAGE_KEYS[rest[0]] || 'shared.news'), heading],
        customDomain,
      ),
      robots: 'noindex, nofollow',
      ogImage: image,
      siteName,
    }
  }

  if (customDomain?.team && rest[0] !== 'teams') {
    return teamPageSeo({
      rest,
      guild,
      team: customDomain.team,
      customDomain,
      heading,
      image,
      siteName,
    })
  }

  if (rest[0] === 'teams' && rest[1]) {
    return teamPageSeo({
      rest: rest.slice(2),
      guild,
      team,
      customDomain,
      heading,
      image,
      siteName,
    })
  }

  return {
    title: siteTitle(
      [
        GUILD_SUBPAGE_KEYS[rest[0]]
          ? t(GUILD_SUBPAGE_KEYS[rest[0]])
          : undefined,
        heading,
      ],
      customDomain,
    ),
    description,
    ogImage: image,
    robots: 'index, follow',
    siteName,
  }
}
